/**
 * Sidebar Component
 * Side navigation panel
 */

import { HTMLAttributes, forwardRef, ReactNode } from 'react'
import { clsx } from 'clsx'

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  width?: number
  collapsed?: boolean
}

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  ({ width = 256, collapsed = false, className, children, ...props }, ref) => {
    return (
      <aside
        ref={ref}
        className={clsx(
          'bg-gray-50 border-r border-gray-200 transition-all duration-300 overflow-auto',
          collapsed ? 'w-16' : '',
          className,
        )}
        style={!collapsed ? { width: `${width}px` } : undefined}
        {...props}
      >
        {children}
      </aside>
    )
  },
)

Sidebar.displayName = 'Sidebar'

export interface SidebarItemProps extends HTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode
  label: string
  active?: boolean
  collapsed?: boolean
}

export const SidebarItem = forwardRef<HTMLButtonElement, SidebarItemProps>(
  ({ icon, label, active = false, collapsed = false, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={clsx(
          'w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors',
          'hover:bg-gray-100',
          active ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600' : 'text-gray-700',
          collapsed && 'justify-center',
          className,
        )}
        title={collapsed ? label : undefined}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {!collapsed && <span className="truncate">{label}</span>}
      </button>
    )
  },
)

SidebarItem.displayName = 'SidebarItem'

export const SidebarSection = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('py-2', className)} {...props}>
        {children}
      </div>
    )
  },
)

SidebarSection.displayName = 'SidebarSection'

export const SidebarHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('px-4 py-3 text-xs font-semibold text-gray-500 uppercase', className)} {...props}>
        {children}
      </div>
    )
  },
)

SidebarHeader.displayName = 'SidebarHeader'
